<script lang="ts">
export default {
  name: 'DisclaimerModal',
};
</script>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue';
import { disclaimerService } from '@/services/v2/DisclaimerService';
import { Disclaimer } from '@/types/Disclaimer';
import { Commit } from '@/store';
import { StoreMutationType } from '@/types/StoreMutationType';
import { signOut } from '@/utilities/SignOut';
import { sanitizeRichText } from '@/utilities/Sanitization';
import AppModal from '@/components/v2/AppModal.vue';

const props = defineProps<{
  disclaimerType: string;
}>();

const disclaimerTitle = 'CAC Legal Disclaimer';
const showModal = ref<boolean>(true);
const disclaimer = ref<Disclaimer>({} as Disclaimer);

onBeforeMount(async () => {
  disclaimer.value = await disclaimerService.getDisclaimerByType(props.disclaimerType);
});

const disclaimerText = computed(() => {
  const text = disclaimer.value?.text?.replace(/(?:\r\n|\r|\n)/g, '<br>') ?? '';
  // Sanitize to prevent XSS while allowing basic formatting
  return sanitizeRichText(text);
});

async function acceptDisclaimer(): Promise<void> {
  await disclaimerService.acceptDisclaimer(disclaimer.value.id);
  Commit(StoreMutationType.ACCEPT_LEGAL_DISCLAIMER);
  showModal.value = false;
}

function logout(): void {
  void signOut();
}
</script>

<template>
  <AppModal
    :show="showModal"
    size="xl"
    cancel-button-label="Reject"
    action-button-label="Accept"
    :handle-cancel="logout"
    :handle-action="acceptDisclaimer"
    data-testid="disclaimer-modal">
    <template #title>
      <h1>{{ disclaimerTitle }}</h1>
    </template>
    <template #content>
      <!-- eslint-disable-next-line vue/no-v-text-v-html-on-component -->
      <div
        v-html="disclaimerText"
        data-testid="disclaimer-text"
        class="prose max-w-none" />
    </template>
  </AppModal>
</template>
